// apps/roleforge-app/src/components/materialize/ProposalReviewPage.tsx
// Task 5 (#1738) — Review screen between "Looks good — lock it in" and
// materialization. Fetches the materialize proposal, renders exactly what
// will be set up, and only the confirm button here starts the progress
// dialog — bound to the Blueprint version that was actually on screen.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { navigateToExternalUrl } from '../../lib/external-navigation';
import {
  fetchMaterializeProposal,
  type MaterializeProposal,
} from '../../lib/materialize-api';
import {
  MaterializeProgressDialog,
  customerSafeMaterializeError,
} from './MaterializeProgressDialog';

/**
 * Frozen at the moment Confirm is clicked. The dialog re-fires its POST when
 * its props change, so the payload must be a snapshot of what was ON SCREEN
 * at click time — a stale re-fetch landing later, or a keyboard reaching the
 * invites checkbox behind the overlay, must never mutate a mounted dialog's
 * props into a confirmation the owner never reviewed.
 */
interface ConfirmPayload {
  blueprintVersion: string;
  sendInvites: boolean;
}

type Seat = MaterializeProposal['seats'][number];

/**
 * `proposal.invites` lists one entry PER SEAT, but the server dedupes
 * per-email when it sends — so the review groups by email to show exactly
 * as many invitations as will actually go out.
 */
export function groupInvitesByEmail(
  invites: MaterializeProposal['invites'],
): Array<{ email: string; assignments: Array<{ seatName: string; roleName: string }> }> {
  const byEmail = new Map<string, Array<{ seatName: string; roleName: string }>>();
  for (const invite of invites) {
    const list = byEmail.get(invite.email) ?? [];
    list.push({ seatName: invite.seatName, roleName: invite.roleName });
    byEmail.set(invite.email, list);
  }
  return [...byEmail.entries()].map(([email, assignments]) => ({ email, assignments }));
}

function SeatCard({ seat }: { seat: Seat }) {
  return (
    <li
      className="rounded-lg border border-line bg-white/5 p-4"
      data-testid={`review-seat-${seat.name}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {seat.emoji && <span aria-hidden="true">{seat.emoji}</span>}
        <span className="font-semibold text-ink">{seat.name}</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${
            seat.level === 'manager'
              ? 'bg-plum/20 text-plum font-semibold'
              : 'bg-line text-graphite'
          }`}
          data-testid="review-seat-level"
        >
          {seat.level}
        </span>
        {seat.compartment && (
          <span
            className="rounded-full bg-ember/10 px-2 py-0.5 text-xs text-ember"
            data-testid="review-seat-compartment"
          >
            need-to-know
          </span>
        )}
        {seat.personaName && (
          <span
            className="rounded-full bg-mint/15 px-2 py-0.5 text-xs text-ink"
            data-testid="review-seat-persona"
          >
            presents publicly as {seat.personaName}
          </span>
        )}
      </div>
      {/* v1 audits carry null humanSummary/aiCharter/personaName — render
          only what IS known so nothing looks empty. */}
      {seat.humanSummary && (
        <p className="mt-2 text-sm text-graphite">
          <span className="font-semibold text-ink">You/your team:</span> {seat.humanSummary}
        </p>
      )}
      {seat.aiCharter && (
        <p className="mt-1 text-sm text-graphite">
          <span className="font-semibold text-ink">Your AI:</span> {seat.aiCharter}
        </p>
      )}
      {seat.skills.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5" data-testid="review-seat-skills">
          {seat.skills.map((skill) => (
            <li
              key={skill}
              className="rounded bg-line/50 px-2 py-0.5 text-xs text-graphite"
            >
              {skill}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function ProposalReviewPage() {
  const { id: auditId } = useParams<{ id: string }>();
  const { jwt } = useAuth();
  const [proposal, setProposal] = useState<MaterializeProposal | null>(null);
  /** Customer-safe copy for rendering; raw string kept for support only. */
  const [error, setError] = useState<{ friendly: string; raw: string } | null>(null);
  /** True while a proposal fetch (initial or stale re-fetch) is in flight. */
  const [refreshing, setRefreshing] = useState(false);
  const [sendInvites, setSendInvites] = useState(true);
  /** Non-null while the progress dialog is up — see ConfirmPayload. */
  const [confirmPayload, setConfirmPayload] = useState<ConfirmPayload | null>(null);
  const [staleMessage, setStaleMessage] = useState<string | null>(null);

  const loadProposal = useCallback(() => {
    if (!auditId || !jwt) return;
    setRefreshing(true);
    fetchMaterializeProposal(auditId, jwt)
      .then((p) => {
        setProposal(p);
        setError(null);
      })
      .catch((e: unknown) =>
        setError({
          friendly: customerSafeMaterializeError(e),
          raw: e instanceof Error ? e.message : String(e),
        }),
      )
      .finally(() => setRefreshing(false));
  }, [auditId, jwt]);

  useEffect(() => {
    loadProposal();
  }, [loadProposal]);

  // 409 blueprint_stale from the POST: the Blueprint moved between review and
  // confirm. Re-fetch and re-render with the server's message — never
  // auto-retry a confirmation the owner has not looked at again.
  const onBlueprintStale = useCallback(
    (message: string) => {
      setConfirmPayload(null);
      setStaleMessage(message);
      loadProposal();
    },
    [loadProposal],
  );

  const seatsByDepartment = useMemo(() => {
    if (!proposal) return [];
    const order = proposal.departments.map((d) => d.name);
    const groups = new Map<string, Seat[]>();
    for (const seat of proposal.seats) {
      const list = groups.get(seat.departmentName) ?? [];
      list.push(seat);
      groups.set(seat.departmentName, list);
    }
    // Departments first in proposal order, then any seat group whose
    // department was not in the departments list.
    const names = [...order, ...[...groups.keys()].filter((n) => !order.includes(n))];
    return names
      .map((name) => ({ name, seats: groups.get(name) ?? [] }))
      .filter((g) => g.seats.length > 0);
  }, [proposal]);

  const groupedInvites = useMemo(
    () => groupInvitesByEmail(proposal?.invites ?? []),
    [proposal],
  );

  if (!auditId) return <Navigate to="/" />;
  if (error) {
    // Customer-safe posture, same as the progress dialog: friendly copy at
    // the owner, raw string in a data- attribute for support only.
    return (
      <div className="p-8" data-testid="review-error" data-raw-error={error.raw}>
        <p className="max-w-2xl text-ember">{error.friendly}</p>
        <button
          type="button"
          onClick={() => {
            setError(null);
            loadProposal();
          }}
          className="mt-4 rounded bg-plum px-4 py-2 text-sm font-semibold text-paper hover:bg-plum/90"
          data-testid="review-error-retry"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!proposal) return <div className="p-8 text-graphite">Loading…</div>;

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="proposal-review-page">
      <main className="mx-auto max-w-3xl px-6 pb-28 pt-12">
        <h1 className="text-3xl font-bold">Review your AI Workforce Blueprint</h1>
        <p className="mt-3 max-w-2xl text-graphite">
          This is what will be set up. Nothing is created until you confirm.
        </p>

        {staleMessage && (
          <div
            role="alert"
            className="mt-6 rounded-lg bg-ember/10 p-4 text-sm text-ember"
            data-testid="review-stale-banner"
          >
            {staleMessage}
          </div>
        )}

        <section className="mt-8" data-testid="review-departments">
          <h2 className="text-xl font-semibold">Departments</h2>
          <ul className="mt-3 space-y-3">
            {proposal.departments.map((dept) => (
              <li
                key={dept.name}
                className="rounded-lg border border-line bg-white/5 p-4"
                data-testid={`review-department-${dept.name}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-ink">{dept.name}</span>
                  {dept.compartment && (
                    <span
                      className="rounded-full bg-ember/10 px-2 py-0.5 text-xs text-ember"
                      data-testid="review-department-compartment"
                    >
                      need-to-know
                    </span>
                  )}
                </div>
                {dept.description && (
                  <p className="mt-1 text-sm text-graphite">{dept.description}</p>
                )}
              </li>
            ))}
          </ul>
        </section>

        {seatsByDepartment.length > 0 && (
          <section className="mt-8" data-testid="review-seats">
            <h2 className="text-xl font-semibold">Seats</h2>
            {seatsByDepartment.map((group) => (
              <div key={group.name} className="mt-4">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-graphite">
                  {group.name}
                </h3>
                <ul className="mt-2 space-y-3">
                  {group.seats.map((seat) => (
                    <SeatCard key={seat.name} seat={seat} />
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        {proposal.invites.length > 0 && (
          <section className="mt-8" data-testid="review-invites">
            <h2 className="text-xl font-semibold">Invitations</h2>
            <label className="mt-3 flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={sendInvites}
                onChange={(e) => setSendInvites(e.target.checked)}
                data-testid="review-send-invites"
              />
              Send invitations now
            </label>
            <ul className="mt-3 space-y-2">
              {groupedInvites.map(({ email, assignments }) => (
                <li
                  key={email}
                  className="rounded-lg border border-line bg-white/5 p-3 text-sm"
                  data-testid={`review-invite-${email}`}
                >
                  <span className="font-semibold text-ink">{email}</span>
                  <span className="text-graphite">
                    {' — '}
                    {assignments
                      .map((a) => `${a.seatName} (${a.roleName})`)
                      .join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {proposal.unassignedPeople.length > 0 && (
          <section className="mt-8" data-testid="review-unassigned">
            <h2 className="text-xl font-semibold">Assign later</h2>
            <p className="mt-1 text-sm text-graphite">
              These people have no email yet — assign later in Workforce settings.
            </p>
            <ul className="mt-3 space-y-2">
              {proposal.unassignedPeople.map((person) => (
                <li
                  key={`${person.name}-${person.seatName}`}
                  className="rounded-lg border border-line bg-white/5 p-3 text-sm text-graphite"
                >
                  <span className="font-semibold text-ink">{person.name}</span>
                  {' — '}
                  {person.seatName}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <footer className="fixed inset-x-0 bottom-0 border-t border-line bg-paper px-6 py-4">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
          <Link
            to={`/audits/${auditId}`}
            className="rounded border border-line px-4 py-2 text-sm text-graphite hover:bg-line/30"
            data-testid="review-back"
          >
            Back to Blueprint
          </Link>
          <button
            type="button"
            // Disabled while a re-fetch is in flight: what is on screen may be
            // about to change, so there is nothing trustworthy to snapshot.
            disabled={refreshing}
            onClick={() => {
              setStaleMessage(null);
              // Snapshot at click time — see ConfirmPayload.
              setConfirmPayload({
                blueprintVersion: proposal.blueprintVersion,
                sendInvites: proposal.invites.length > 0 && sendInvites,
              });
            }}
            className="rounded bg-plum px-4 py-2 text-sm font-semibold text-paper hover:bg-plum/90 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="review-confirm"
          >
            Confirm and build
          </button>
        </div>
      </footer>

      {confirmPayload && jwt && (
        <MaterializeProgressDialog
          auditId={auditId}
          jwt={jwt}
          blueprintVersion={confirmPayload.blueprintVersion}
          sendInvites={confirmPayload.sendInvites}
          onBlueprintStale={onBlueprintStale}
          onCancel={() => setConfirmPayload(null)}
          onRedirect={navigateToExternalUrl}
        />
      )}
    </div>
  );
}
